"use client";

import { useState, useEffect } from "react";
import styles from "./page.module.css";

interface Practice {
  _id: string;
  title: string;
  summary?: string;
  objective?: string;
  whatYouDid?: string;
  howYouDid?: string;
  whyItWorked?: string;
  innovativeAspects?: string;
  replicability?: string;
  resources?: string;
  addedValue?: string;
  results?: string;
  institution?: string;
  submitterName?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export default function GoodPractices() {
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("published");
  const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const itemsPerPage = 6;
  const DEFAULT_INSTITUTION = "Ayuntamiento de Madrid";
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    title: "",
    institution: "",
    role: "",
    name: "",
    email: "",
    summary: "",
    objective: "",
    whatYouDid: "",
    howYouDid: "",
    whyItWorked: "",
    innovativeAspects: "",
    replicability: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setCurrentPage(1); // Resetear a primera página al buscar
      loadPractices();
    }, 300); // Esperar 300ms después de que el usuario deje de escribir

    return () => clearTimeout(delaySearch);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalOpen]);

  const loadPractices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (statusFilter) params.append("status", statusFilter);
      
      const res = await fetch(`${basePath}/api/good-practices?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPractices(data);
      }
    } catch (error) {
      console.error("Error cargando prácticas:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const res = await fetch(`${basePath}/api/good-practices/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        setSubmitSuccess(true);
        // Resetear formulario
        setFormData({
          title: "",
          institution: "",
          role: "",
          name: "",
          email: "",
          summary: "",
          objective: "",
          whatYouDid: "",
          howYouDid: "",
          whyItWorked: "",
          innovativeAspects: "",
          replicability: "",
        });
        // Scroll al mensaje de éxito
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      } else {
        setSubmitError(data.error || "Error al enviar la práctica");
      }
    } catch (error) {
      console.error("Error enviando práctica:", error);
      setSubmitError("Error de conexión. Por favor, intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = async (practiceId: string) => {
    try {
      const res = await fetch(`${basePath}/api/good-practices/${practiceId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPractice(data);
        setModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevenir scroll del body
      }
    } catch (error) {
      console.error("Error cargando detalles:", error);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPractice(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className="container">
          <h1 className={styles.heroTitle}>Buenas Prácticas</h1>
          <p className={styles.heroSubtitle}>
            Descubre experiencias exitosas y soluciones innovadoras compartidas por profesionales y organizaciones
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="section">
        <div className="container">
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="Buscar por título o resumen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            
            <div className={styles.controls}>
              <button
                onClick={() => setShowAll(!showAll)}
                className={`${styles.showAllButton} ${showAll ? styles.showAllActive : ''}`}
              >
                {showAll ? "Mostrar paginado" : "Mostrar todos"}
              </button>
              
              {!showAll && practices.length > itemsPerPage && (
              <div className={styles.paginationControls}>
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                  title="Página anterior"
                >
                  ←
                </button>
                
                <span className={styles.pageIndicator}>
                  {currentPage}/{Math.ceil(practices.length / itemsPerPage)}
                </span>
                
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(practices.length / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(practices.length / itemsPerPage)}
                  className={styles.paginationButton}
                  title="Página siguiente"
                >
                  →
                </button>
              </div>
            )}
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Cargando prácticas...</p>
            </div>
          ) : practices.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>📋</div>
              <h3>No se encontraron prácticas</h3>
              <p>Intenta con otros términos de búsqueda</p>
            </div>
          ) : (
            <>
              <div className={styles.grid}>
                {(showAll ? practices : practices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)).map((practice) => (
                  <div key={practice._id} className={styles.card}>
                    <div className={styles.cardHeader}>
                      <span className={styles.badge}>
                        {practice.status === "published" ? "Publicado" : "Borrador"}
                      </span>
                      <span className={styles.date}>
                        {new Date(practice.updatedAt).toLocaleDateString("es-ES")}
                      </span>
                    </div>
                    <h3 className={styles.cardTitle}>{practice.title}</h3>
                    <p className={styles.modalInstitution}>{practice.institution || DEFAULT_INSTITUTION}</p>
                    <p className={styles.cardSummary}>
                      {practice.summary || "Sin descripción"}
                    </p>
                    <button 
                      onClick={() => openModal(practice._id)} 
                      className={styles.cardLink}
                    >
                      Ver detalles →
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Share Form Section */}
      <section className="section section-light" id="compartir">
        <div className="container">
          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2>¡Comparte tu Experiencia!</h2>
              <p>
                Si llevaste a cabo una práctica innovadora en tu institución, puedes enviarla directamente al banco. 
                Solo tienes que completar el formulario. Nos interesa lo que hiciste, cómo lo hiciste y por qué funcionó.
              </p>
            </div>

            {submitSuccess && (
              <div className={styles.successMessage}>
                <strong>✅ ¡Gracias por tu aportación!</strong>
                <p>Tu buena práctica ha sido enviada correctamente y será revisada por nuestro equipo antes de publicarse.</p>
              </div>
            )}

            {submitError && (
              <div className={styles.errorMessage}>
                <strong>❌ Error:</strong> {submitError}
              </div>
            )}

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="title">Título de la práctica *</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title" 
                    required 
                    placeholder="Nombre de tu práctica innovadora"
                    value={formData.title}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="institution">Institución / Organización *</label>
                  <input 
                    type="text" 
                    id="institution" 
                    name="institution" 
                    required 
                    placeholder="Nombre de tu organización"
                    value={formData.institution}
                    onChange={handleFormChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="role">Tu rol / cargo</label>
                  <input 
                    type="text" 
                    id="role" 
                    name="role" 
                    placeholder="Tu posición en la organización"
                    value={formData.role}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">Nombre y apellido *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    placeholder="Tu nombre completo"
                    value={formData.name}
                    onChange={handleFormChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email de contacto *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="summary">Resumen de la práctica *</label>
                <textarea 
                  id="summary" 
                  name="summary" 
                  rows={4} 
                  required
                  placeholder="Describe brevemente tu práctica innovadora..."
                  value={formData.summary}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="objective">Objetivo</label>
                <textarea 
                  id="objective" 
                  name="objective" 
                  rows={3}
                  placeholder="¿Qué querías lograr con esta práctica?"
                  value={formData.objective}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="whatYouDid">¿Qué hiciste?</label>
                <textarea 
                  id="whatYouDid" 
                  name="whatYouDid" 
                  rows={4}
                  placeholder="Describe las acciones que llevaste a cabo..."
                  value={formData.whatYouDid}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="howYouDid">¿Cómo lo hiciste?</label>
                <textarea 
                  id="howYouDid" 
                  name="howYouDid" 
                  rows={4}
                  placeholder="Explica la metodología y los recursos utilizados..."
                  value={formData.howYouDid}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="whyItWorked">¿Por qué funcionó?</label>
                <textarea 
                  id="whyItWorked" 
                  name="whyItWorked" 
                  rows={4}
                  placeholder="¿Qué resultados obtuviste? ¿Qué factores clave contribuyeron al éxito?"
                  value={formData.whyItWorked}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="innovativeAspects">Aspectos Innovadores</label>
                <textarea 
                  id="innovativeAspects" 
                  name="innovativeAspects" 
                  rows={3}
                  placeholder="¿Qué hace a esta práctica innovadora?"
                  value={formData.innovativeAspects}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="replicability">Replicabilidad</label>
                <textarea 
                  id="replicability" 
                  name="replicability" 
                  rows={3}
                  placeholder="¿Puede esta práctica ser implementada en otros contextos? ¿Cómo?"
                  value={formData.replicability}
                  onChange={handleFormChange}
                />
              </div>

              <div className={styles.formActions}>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={submitting}
                >
                  {submitting ? "Enviando..." : "Enviar Práctica"}
                </button>
                <p className={styles.formNote}>
                  * Campos obligatorios. Tu aporte nutre al banco y a quienes lo consultan.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Modal para ver detalles */}
      {modalOpen && selectedPractice && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={closeModal}>
              ✕
            </button>
            
            <div className={styles.modalHeader}>
              <h2>{selectedPractice.title}</h2>
              <p className={styles.modalInstitution}>{selectedPractice.institution || DEFAULT_INSTITUTION}</p>
              <span className={styles.modalDate}>
                Publicado el {new Date(selectedPractice.updatedAt).toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                })}
              </span>
            </div>

            <div className={styles.modalBody}>
              {selectedPractice.summary && (
                <div className={styles.modalSection}>
                  <h3>Resumen</h3>
                  <p>{selectedPractice.summary}</p>
                </div>
              )}

              {selectedPractice.objective && (
                <div className={styles.modalSection}>
                  <h3>Objetivo</h3>
                  <p>{selectedPractice.objective}</p>
                </div>
              )}

              {selectedPractice.whatYouDid && (
                <div className={styles.modalSection}>
                  <h3>¿Qué se hizo?</h3>
                  <p>{selectedPractice.whatYouDid}</p>
                </div>
              )}

              {selectedPractice.howYouDid && (
                <div className={styles.modalSection}>
                  <h3>¿Cómo se hizo?</h3>
                  <p>{selectedPractice.howYouDid}</p>
                </div>
              )}

              {selectedPractice.whyItWorked && (
                <div className={styles.modalSection}>
                  <h3>¿Por qué funcionó?</h3>
                  <p>{selectedPractice.whyItWorked}</p>
                </div>
              )}

              {selectedPractice.innovativeAspects && (
                <div className={styles.modalSection}>
                  <h3>Aspectos Innovadores</h3>
                  <p>{selectedPractice.innovativeAspects}</p>
                </div>
              )}

              {selectedPractice.replicability && (
                <div className={styles.modalSection}>
                  <h3>Replicabilidad</h3>
                  <p>{selectedPractice.replicability}</p>
                </div>
              )}

              {selectedPractice.resources && (
                <div className={styles.modalSection}>
                  <h3>Recursos Necesarios</h3>
                  <p>{selectedPractice.resources}</p>
                </div>
              )}

              {selectedPractice.addedValue && (
                <div className={styles.modalSection}>
                  <h3>Valor Añadido</h3>
                  <p>{selectedPractice.addedValue}</p>
                </div>
              )}

              {selectedPractice.results && (
                <div className={styles.modalSection}>
                  <h3>Resultados</h3>
                  <p>{selectedPractice.results}</p>
                </div>
              )}

              {selectedPractice.submitterName && (
                <div className={styles.modalFooter}>
                  <p><strong>Compartido por:</strong> {selectedPractice.submitterName}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
